import React, { useState } from 'react'
import styled from 'styled-components'
import range from 'lodash/range'

import { connect } from 'react-redux'
import { I18nComponentType } from 'i18n/i18n-types'
import { mapLanguageToComponentProps } from 'i18n'
import { VisiblyHidden } from 'theme/accessibility'
import { StarIcon, StarBox, StyledOutlineStar, IconSize } from './star-icon'

export const NUM_OF_STARS = 5

const RatingStarsGroup = styled.div`
  display: flex;
`

interface RatingStarsProps {
  i18n: I18nComponentType
  ownRating?: number
  average?: number
  onRate?: (rate: number) => void
  onResetRate?: () => void
  size?: IconSize
  isLoading?: boolean
  showAverage?: boolean
}

export const RatingStar = styled.label<{ $disabled: boolean }>`
  padding: 0;
  margin-bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
`

const VisiblyHiddenInput = styled.input`
  ${VisiblyHidden}

  &.focus-visible + ${StarBox} {
    ${StyledOutlineStar} {
      visibility: visible;
    }
  }
`

const VisiblyHiddenLabel = styled.span`
  ${VisiblyHidden}
`

const getPercentage = (average: number, index: number) => {
  if (average >= index + 1) {
    return 100
  }
  if (average > index && average < index + 1) {
    const diff = Math.floor(average)
    return Math.ceil((average - diff) * 100)
  }
  return 0
}

const RatingStarsFC: React.FC<RatingStarsProps> = ({
  i18n,
  ownRating,
  average,
  onRate,
  size = 'default',
  isLoading = false,
  showAverage = true,
}) => {
  const [hoverIndex, setHoverIndex] = useState<number | undefined>(undefined)

  const onHover = (index: number | undefined) => {
    setHoverIndex(index)
  }

  return (
    <RatingStarsGroup role="radiogroup" aria-label={i18n.rating.ownVote}>
      {range(NUM_OF_STARS).map(index => (
        <RatingStar
          key={index}
          $disabled={!onRate || isLoading}
          onMouseEnter={() => !!onRate && onHover(index)}
          onMouseLeave={() => !!onRate && onHover(undefined)}
        >
          <VisiblyHiddenInput
            type="radio"
            value={index + 1}
            checked={ownRating === index + 1}
            disabled={!onRate || isLoading}
            onChange={() => onRate?.(index + 1)}
            onFocus={() => !!onRate && onHover(index)}
            onBlur={() => !!onRate && onHover(undefined)}
          />
          <StarIcon
            size={size}
            hasOwnRating={!!ownRating}
            isHovered={hoverIndex !== undefined && hoverIndex >= index}
            isHoveringStars={hoverIndex !== undefined}
            percentage={showAverage ? getPercentage(average || 0, index) : 0}
            disabled={isLoading}
          />
          <VisiblyHiddenLabel>{index + 1}</VisiblyHiddenLabel>
        </RatingStar>
      ))}
    </RatingStarsGroup>
  )
}

export const RatingStars = connect<{
  i18n: I18nComponentType
}>(mapLanguageToComponentProps())(RatingStarsFC)
